import { getCore } from '../../../../client/core/getCore';
import type { DynamicClient } from '../../../../client/types';
import { APIError } from '../../../../errors/APIError';
import { getWalletBookCdnUrl } from '../../getWalletBookCdnUrl';
import type { WalletBookSchema } from '../../legacyWalletBook.types';

/**
 * The timeout for each fetch attempt in milliseconds.
 */
const FETCH_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Fetches the wallet book from the CDN endpoint with timeout handling.
 *
 * @param client - The Dynamic client instance.
 * @returns A promise that resolves to the wallet book data.
 */
export const fetchWithTimeout = async (
  client: DynamicClient
): Promise<WalletBookSchema> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  const core = getCore(client);

  try {
    const response = await core.fetch(getWalletBookCdnUrl(), {
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch wallet book: ${response.status} ${response.statusText}`,
        'wallet_book_fetch_error',
        response.status
      );
    }

    const data = (await response.json()) as WalletBookSchema;

    return data;
  } finally {
    clearTimeout(timeoutId);
  }
};
